import { Link, useLocation, useParams } from "react-router-dom";
import { useRef } from "react";
import Footer from "../../Components/common/Footer";
import Header from "../../Components/common/Header";
import Layout from "../../Components/Website/Layout";
import { useEffect, useState, useMemo, useCallback } from "react";
import { baseURL, CHAPTERID, VIDIO } from "../../API/API";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
import VideoPlayer from "./videoPlayer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faBook,
} from "@fortawesome/free-solid-svg-icons";
import "./Lesson.css";
import { toast } from "react-toastify";
import { getYoutubeVideoId, toYoutubeEmbedUrl } from "../../utils/youtube";

function normalizeLesson(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    name: raw.name ?? raw.lesson_name ?? "درس",
    description: raw.description ?? raw.lesson_description ?? "",
    content: raw.content ?? raw.lesson_content ?? raw.lesson_description ?? "",
    video_url: raw.video_url ?? raw.lesson_video_url ?? "",
    chapter_id: raw.chapter_id,
    academic_year_id: raw.academic_year_id,
    sort_order: raw.sort_order ?? raw.order ?? 0,
    assignment_title: raw.assignment_title,
    assignment_description: raw.assignment_description,
    assignment_file_path: raw.assignment_file_path,
    student_submission: raw.student_submission,
  };
}

export default function Lesson() {
  const axiosPrivate = useAxiosPrivate();
  const { id } = useParams();
  const [lessonData, setLessonData] = useState(null);
  const [chapterLessons, setChapterLessons] = useState([]);
  const [isLessonComplete, setIsLessonComplete] = useState(false);
  const playerRef = useRef(null);
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("content");
  const [assignmentFile, setAssignmentFile] = useState(null);
  const [submittingAssignment, setSubmittingAssignment] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketMessage, setTicketMessage] = useState("");
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [showExamGateway, setShowExamGateway] = useState(false);

  const academicId =
    location?.state?.academicYearId ??
    location?.state?.id ??
    lessonData?.academic_year_id;
  const chapterId = location?.state?.chapterId ?? lessonData?.chapter_id;
  const returnTo = location?.state?.returnTo || (academicId ? `/learn/${academicId}` : "/courses");
  const returnLabel = location?.state?.returnLabel || "العودة للوحدة";

  const embedUrl = useMemo(
    () => toYoutubeEmbedUrl(lessonData?.video_url),
    [lessonData?.video_url]
  );
  const videoId = useMemo(
    () => getYoutubeVideoId(lessonData?.video_url),
    [lessonData?.video_url]
  );
  const { auth } = useAuth();
  const userKey = auth?.user?.email || "guest";
  const lessonStorageKey = `completed-lesson-${userKey}-${id}`;

  const fetchLessonTickets = useCallback(async () => {
    setLoadingTickets(true);
    try {
      const res = await axiosPrivate.get(`${baseURL}/learning/return-response?lesson_id=${id}`);
      setTickets(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setLoadingTickets(false);
    }
  }, [id, axiosPrivate]);

  useEffect(() => {
    if (activeTab === "qna" && id) {
      fetchLessonTickets();
    }
  }, [activeTab, id, fetchLessonTickets]);

  const handleOpenTicket = async (e) => {
    e.preventDefault();
    if (!ticketMessage.trim()) return;

    setSubmittingTicket(true);
    try {
      await axiosPrivate.post(`${baseURL}/learning/send-message`, {
        message: ticketMessage,
        selectId: parseInt(id),
      });
      setTicketMessage("");
      toast.success("تم إرسال استفسارك بنجاح");
      fetchLessonTickets();
    } catch (error) {
      console.error("Error opening ticket:", error);
      const errorMsg = error?.response?.data?.message || "فشل إرسال الاستفسار";
      toast.error(errorMsg);
    } finally {
      setSubmittingTicket(false);
    }
  };

  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    if (!assignmentFile) return;

    setSubmittingAssignment(true);
    const formData = new FormData();
    formData.append("screenshot", assignmentFile);

    try {
      const res = await axiosPrivate.post(
        `${baseURL}/learning/lesson/${id}/submit-assignment`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("تم تسليم الواجب بنجاح");
      setLessonData((prev) => ({
        ...prev,
        student_submission: res.data.data,
      }));
    } catch (error) {
      console.error("Error submitting assignment:", error);
      const errorMsg = error?.response?.data?.message || "فشل إرسال الواجب";
      toast.error(errorMsg);
    } finally {
      setSubmittingAssignment(false);
    }
  };

  const sortedLessons = useMemo(
    () =>
      [...chapterLessons].sort(
        (a, b) => (a.sort_order ?? a.id) - (b.sort_order ?? b.id)
      ),
    [chapterLessons]
  );

  const currentIndex = sortedLessons.findIndex(
    (l) => Number(l.id) === Number(id)
  );
  const prevLesson =
    currentIndex > 0 ? sortedLessons[currentIndex - 1] : null;
  const nextLesson =
    currentIndex >= 0 && currentIndex < sortedLessons.length - 1
      ? sortedLessons[currentIndex + 1]
      : null;

  useEffect(() => {
    async function fetchLesson() {
      try {
        const res = await axiosPrivate.get(`${baseURL}/${VIDIO}/${id}`);

        if (!res.data?.data) {
          toast.error("الدرس غير موجود");
          return;
        }

        setLessonData(normalizeLesson(res.data.data));
      } catch (error) {
        console.error("خطأ في تحميل الدرس:", error);
        const errorMsg =
          error?.response?.status === 404
            ? "الدرس غير موجود"
            : error?.response?.status === 403
            ? "غير مصرح بالوصول لهذا الدرس"
            : "فشل تحميل الدرس";
        toast.error(errorMsg);
      }
    }

    if (id) {
      fetchLesson();
    }
  }, [id, axiosPrivate]);

  useEffect(() => {
    setIsLessonComplete(localStorage.getItem(lessonStorageKey) === "true");
  }, [lessonStorageKey]);

  useEffect(() => {
    if (lessonData && !embedUrl && !videoId) {
      setIsLessonComplete(true);
      localStorage.setItem(lessonStorageKey, "true");
    }
  }, [embedUrl, lessonData, lessonStorageKey, videoId]);

  const markLessonComplete = useCallback(() => {
    setIsLessonComplete(true);
    localStorage.setItem(lessonStorageKey, "true");
  }, [lessonStorageKey]);

  const handleNextLessonClick = (event) => {
    if (!isLessonComplete) {
      event.preventDefault();
      toast.error("أكمل هذا الدرس أولاً قبل الانتقال للتالي", {
        style: {
          direction: "rtl",
        },
      });
    }
  };

  const handleExamClick = (event) => {
    if (!isLessonComplete) {
      event.preventDefault();
      toast.error("أكمل هذا الدرس أولاً قبل الانتقال للاختبار التقييمي", {
        style: {
          direction: "rtl",
        },
      });
    }
  };

  const handleTestUnderstandingClick = () => {
    if (!isLessonComplete) {
      toast.error("أكمل هذا الدرس أولاً قبل الانتقال لاختبار الفهم", {
        style: {
          direction: "rtl",
        },
      });
      return;
    }
    setShowExamGateway(true);
  };

  useEffect(() => {
    async function fetchChapterLessons() {
      const cid = chapterId ?? lessonData?.chapter_id;
      if (!cid) return;

      try {
        const res = await axiosPrivate.get(`${baseURL}/${CHAPTERID}/${cid}`);
        const rows = (res.data?.data || []).map((row) => ({
          id: row.id,
          name: row.lesson_name ?? row.name,
          sort_order: row.sort_order ?? row.order ?? row.id,
        }));
        setChapterLessons(rows);
      } catch (error) {
        console.error("خطأ في جلب دروس الوحدة:", error);
      }
    }

    fetchChapterLessons();
  }, [chapterId, lessonData?.chapter_id, axiosPrivate]);

  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handleContextMenu);
    return () => document.removeEventListener("contextmenu", handleContextMenu);
  }, []);

  const lessonNavState = {
    academicYearId: academicId,
    chapterId: chapterId ?? lessonData?.chapter_id,
    returnTo,
    returnLabel,
  };

  return (
    <>
      <Header />
      <Layout>
        <Link to={returnTo} title={returnLabel}>
          <div className="m-4 text-start fw-b">
            <FontAwesomeIcon icon={faArrowLeft} className="arrow-icon" />
          </div>
        </Link>
        {!lessonData ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "60vh" }}
          >
            <p style={{ fontSize: "1.2rem", color: "#555" }}>
              جاري تحميل الدرس...
            </p>
          </div>
        ) : showExamGateway ? (
          <div
            className="exam-gateway-wrapper text-center animate-fade-in"
            style={{
              padding: "4rem 2rem",
              maxWidth: "600px",
              margin: "40px auto",
              backgroundColor: "#fff",
              borderRadius: "16px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
              border: "1px solid #e2e8f0",
            }}
            dir="rtl"
          >
            <div className="mb-4" style={{ fontSize: "3.5rem", color: "#6b0824" }}>
              📝
            </div>
            
            <h2 className="fw-bold text-dark mb-3" style={{ fontSize: "1.75rem" }}>
              اختبار فهمك واستيعابك للدروس
            </h2>
            
            <p className="text-muted mb-4" style={{ fontSize: "1.05rem", lineHeight: "1.8" }}>
              لقد أكملت مشاهدة جميع دروس هذه الوحدة بنجاح! جاهز لتحدي نفسك؟
            </p>
            
            <div className="exam-start-block" style={{ borderTop: "none", marginTop: 0, paddingTop: 0 }}>
              <Link
                to={`/exam/${chapterId ?? lessonData?.chapter_id}`}
                state={{ id: academicId }}
                onClick={handleExamClick}
                className="btn-exam-start-nav"
              >
                ابدأ الامتحان 📝
              </Link>
              <p className="exam-start-subtitle" style={{ marginTop: "12px" }}>
                تأكد من استيعابك لكافة الدروس لتختبر فهمك للوحدة الآن
              </p>
            </div>
            
            <div className="mt-5 border-top pt-3">
              <button
                onClick={() => setShowExamGateway(false)}
                className="btn btn-link text-decoration-none fw-bold"
                style={{ color: "#64748b" }}
              >
                ← الرجوع للدرس
              </button>
            </div>
          </div>
        ) : (
          <div
            className="lesson-wrapper"
            style={{
              padding: "2rem 1rem",
              maxWidth: "900px",
              margin: "0 auto",
            }}
          >
            <div
              className="video-container"
              ref={playerRef}
              style={{
                position: "relative",
                paddingTop: embedUrl || videoId ? "56.25%" : "0",
                marginBottom: "1.5rem",
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                minHeight: embedUrl || videoId ? undefined : "200px",
                background: "#111",
              }}
            >
              {embedUrl || videoId ? (
                <VideoPlayer
                  url={embedUrl || lessonData.video_url}
                  containerRef={playerRef}
                  onComplete={markLessonComplete}
                />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center text-white"
                  style={{ minHeight: "200px" }}
                >
                  لا يوجد فيديو لهذا الدرس
                </div>
              )}
            </div>

            <div className="lesson-meta mb-4" dir="rtl">
              <h1
                className="lesson-title mb-3"
                style={{ fontSize: "1.85rem", color: "#222", fontWeight: 800 }}
              >
                {lessonData.name}
              </h1>
            </div>

            {/* Beautiful Navigation Tabs */}
            <div className="lesson-tabs d-flex border-bottom mb-4" dir="rtl" style={{ gap: "10px" }}>
              <button 
                className={`tab-btn py-3 px-4 fw-bold border-0 bg-transparent position-relative ${activeTab === "content" ? "active text-purple" : "text-muted"}`}
                onClick={() => setActiveTab("content")}
                style={{ transition: "all 0.3s ease", outline: "none" }}
              >
                محتوى الدرس
                {activeTab === "content" && <span className="active-bar" style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "3px", backgroundColor: "var(--brand-purple)" }} />}
              </button>
              <button 
                className={`tab-btn py-3 px-4 fw-bold border-0 bg-transparent position-relative ${activeTab === "assignment" ? "active text-purple" : "text-muted"}`}
                onClick={() => setActiveTab("assignment")}
                style={{ transition: "all 0.3s ease", outline: "none" }}
              >
                الواجب الدراسي
                {lessonData.assignment_title && <span className="notification-dot bg-warning" style={{ position: "absolute", top: "12px", left: "6px", width: "8px", height: "8px", borderRadius: "50%" }} />}
                {activeTab === "assignment" && <span className="active-bar" style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "3px", backgroundColor: "var(--brand-purple)" }} />}
              </button>
              <button 
                className={`tab-btn py-3 px-4 fw-bold border-0 bg-transparent position-relative ${activeTab === "qna" ? "active text-purple" : "text-muted"}`}
                onClick={() => setActiveTab("qna")}
                style={{ transition: "all 0.3s ease", outline: "none" }}
              >
                الاستفسارات والدعم
                {activeTab === "qna" && <span className="active-bar" style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "3px", backgroundColor: "var(--brand-purple)" }} />}
              </button>
            </div>

            {/* Tab Panes */}
            <div className="tab-content-wrapper mb-5" dir="rtl">
              {activeTab === "content" && (
                <div className="tab-pane-content text-end animate-fade-in">
                  {lessonData.description ? (
                    <p
                      className="lesson-description text-muted mb-4"
                      style={{ fontSize: "1.05rem", lineHeight: 1.8 }}
                    >
                      {lessonData.description}
                    </p>
                  ) : null}
                  {lessonData.content ? (
                    <div
                      className="lesson-body-content p-4 rounded-3 bg-light border"
                      style={{ fontSize: "1.05rem", color: "#333", lineHeight: 1.8 }}
                    >
                      <h5 className="fw-bold mb-3 text-dark border-bottom pb-2">تفاصيل المحتوى الدراسي</h5>
                      <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                        {lessonData.content}
                      </p>
                    </div>
                  ) : (
                    <p className="text-muted text-center py-4">لا يوجد تفاصيل إضافية لهذا الدرس.</p>
                  )}
                </div>
              )}

              {activeTab === "assignment" && (
                <div className="tab-pane-content text-end animate-fade-in">
                  {lessonData.assignment_title ? (
                    <div className="assignment-box p-4 rounded-4 bg-white border shadow-sm">
                      <h4 className="fw-bold text-dark mb-3">{lessonData.assignment_title}</h4>
                      <p className="text-muted mb-4" style={{ lineHeight: "1.8" }}>
                        {lessonData.assignment_description}
                      </p>

                      {lessonData.assignment_file_path && (
                        <div className="mb-4">
                          <a 
                            href={lessonData.assignment_file_path} 
                            target="_blank" 
                            rel="noreferrer"
                            className="btn btn-outline-purple d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill text-decoration-none"
                          >
                            <span>تحميل ملف الواجب (PDF)</span>
                            <FontAwesomeIcon icon={faBook} />
                          </a>
                        </div>
                      )}

                      <hr className="my-4" />

                      {/* Submission section */}
                      {lessonData.student_submission ? (
                        <div className="submission-status-wrapper p-3 rounded-3 bg-light border">
                          <h5 className="fw-bold mb-3">حالة تسليم الواجب:</h5>
                          <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                            <span className={`badge ${
                              lessonData.student_submission.status === "graded" ? "bg-success" : "bg-warning"
                            } fs-6 px-3 py-2`}>
                              {lessonData.student_submission.status === "graded" ? "تم التصحيح" : "قيد المراجعة والتقييم"}
                            </span>
                            <span className="text-muted small">
                              تاريخ التسليم: {lessonData.student_submission.created_at}
                            </span>
                          </div>

                          <div className="mb-2">
                            <a 
                              href={lessonData.student_submission.file_path} 
                              target="_blank" 
                              rel="noreferrer"
                              className="text-primary small text-decoration-underline"
                            >
                              عرض الملف المرفوع
                            </a>
                          </div>

                          {lessonData.student_submission.status === "graded" && (
                            <div className="grading-box mt-3 p-3 rounded bg-white border-start border-success border-4">
                              <div className="d-flex justify-content-between mb-2">
                                <span className="fw-bold text-success">الدرجة: {lessonData.student_submission.grade} / 10</span>
                                <span className="text-muted small">تقييم المعلم</span>
                              </div>
                              {lessonData.student_submission.feedback && (
                                <p className="m-0 small text-dark mt-1">
                                  <strong>ملاحظات المصحح: </strong>
                                  {lessonData.student_submission.feedback}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="upload-submission-box">
                          <h5 className="fw-bold mb-3 text-dark">تسليم الواجب:</h5>
                          <form onSubmit={handleAssignmentSubmit}>
                            <div className="mb-3">
                              <label className="form-label text-muted small">ارفع ملف الإجابة (أي نوع ملف، بحد أقصى 100 ميجابايت):</label>
                              <input 
                                type="file" 
                                className="form-control" 
                                required
                                onChange={(e) => setAssignmentFile(e.target.files[0])}
                              />
                            </div>
                            <button 
                              type="submit" 
                              className="btn btn-purple rounded-pill px-4 py-2 text-white fw-bold"
                              disabled={submittingAssignment}
                            >
                              {submittingAssignment ? "جاري إرسال الواجب..." : "إرسال الإجابة لتصحيحها"}
                            </button>
                          </form>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-5 text-center border rounded-4 bg-light">
                      <FontAwesomeIcon icon={faBook} className="text-muted mb-3" style={{ fontSize: "2.5rem" }} />
                      <h5 className="fw-bold text-dark mb-2">لا يوجد واجب محدد</h5>
                      <p className="text-muted m-0 small">لم يقم المدرس برفع واجب لهذا الدرس بعد.</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "qna" && (
                <div className="tab-pane-content text-end animate-fade-in">
                  <div className="qna-wrapper p-4 rounded-4 bg-white border shadow-sm">
                    <h4 className="fw-bold text-dark mb-3">تذاكر الاستفسارات والدعم الأكاديمي</h4>
                    <p className="text-muted mb-4 small">
                      لديك أي استفسار أو نقطة غير واضحة بالفيديو؟ افتح تذكرة جديدة وسيقوم مستر محمود أو أحد المساعدين بالرد عليك وتوضيحها فوراً.
                    </p>

                    {/* Create Ticket Form */}
                    <form onSubmit={handleOpenTicket} className="mb-4 p-3 rounded-3 bg-light border">
                      <h5 className="fw-bold mb-3 text-dark" style={{ fontSize: "1.1rem" }}>فتح استفسار جديد:</h5>
                      <div className="mb-3">
                        <textarea
                          className="form-control"
                          rows={3}
                          required
                          placeholder="اكتب سؤالك أو استفسارك بالتفصيل هنا..."
                          value={ticketMessage}
                          onChange={(e) => setTicketMessage(e.target.value)}
                        />
                      </div>
                      <button 
                        type="submit" 
                        className="btn btn-purple rounded-pill px-4 py-2 text-white fw-bold btn-sm"
                        disabled={submittingTicket}
                      >
                        {submittingTicket ? "جاري الإرسال..." : "إرسال الاستفسار"}
                      </button>
                    </form>

                    <hr className="my-4" />

                    {/* Tickets List */}
                    <h5 className="fw-bold mb-3 text-dark">استفساراتك السابقة لهذا الدرس:</h5>
                    {loadingTickets ? (
                      <div className="text-center py-4">
                        <span className="spinner-border spinner-border-sm text-primary" role="status" />
                        <span className="ms-2 small text-muted">جاري تحميل الاستفسارات...</span>
                      </div>
                    ) : tickets.length > 0 ? (
                      <div className="tickets-list d-flex flex-column gap-3">
                        {tickets.map((t) => (
                          <div key={t.id} className="ticket-card p-3 rounded-3 border bg-white">
                            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                              <span className={`badge ${
                                t.status === "replied" ? "bg-success" : t.status === "closed" ? "bg-secondary" : "bg-warning"
                              } px-2 py-1`}>
                                {t.status === "replied" ? "تم الرد" : t.status === "closed" ? "مغلقة" : "مفتوحة"}
                              </span>
                              <span className="text-muted small">{t.createdAt}</span>
                            </div>
                            
                            <p className="m-0 text-dark fw-bold mb-2" style={{ fontSize: "0.95rem" }}>
                              <strong>سؤالك: </strong>
                              {t.originalMessage}
                            </p>

                            {t.message ? (
                              <div className="admin-reply-box p-3 rounded-3 bg-light border-start border-purple border-4 mt-2">
                                <span className="d-block fw-bold text-purple mb-1" style={{ fontSize: "0.85rem" }}>رد الدعم الأكاديمي:</span>
                                <p className="m-0 small text-dark" style={{ lineHeight: "1.6" }}>{t.message}</p>
                              </div>
                            ) : (
                              <span className="text-muted small d-block mt-2 italic">بانتظار رد المعلم أو المساعدين...</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted text-center py-4 small">لم تقم بإرسال أي استفسارات عن هذا الدرس بعد.</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div
              className="d-flex justify-content-between align-items-center gap-3 flex-wrap mt-4"
              dir="rtl"
            >
              {prevLesson ? (
                <Link
                  to={`/lesson/${prevLesson.id}`}
                  state={lessonNavState}
                  className="btn lesson-nav-btn prev d-flex align-items-center gap-2"
                >
                  <FontAwesomeIcon icon={faArrowRight} />
                  الدرس السابق
                </Link>
              ) : (
                <span />
              )}

              {nextLesson ? (
                <Link
                  to={`/lesson/${nextLesson.id}`}
                  state={lessonNavState}
                  onClick={handleNextLessonClick}
                  className="btn lesson-nav-btn next d-flex align-items-center gap-2"
                >
                  الدرس التالي
                  <FontAwesomeIcon icon={faArrowLeft} />
                </Link>
              ) : (
                <button
                  onClick={handleTestUnderstandingClick}
                  className="btn lesson-nav-btn next d-flex align-items-center gap-2"
                  style={{
                    backgroundColor: "#6b0824",
                    borderColor: "#6b0824",
                    color: "#fff",
                  }}
                >
                  اختبر فهمك
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>
              )}
            </div>
          </div>
        )}
      </Layout>
      <Footer />
    </>
  );
}
